/**
 * ChapterSection — ein Kapitel der Startseite (Plan 2026-08-12-startseite-kapitel).
 *
 * Konzept § 5 hat acht Sektionen beschlossen; gebaut wurden vierzehn. Keine der
 * sechs Ergaenzungen war fuer sich falsch — jede ist einzeln begruendet. Was
 * fehlte, war der Blick auf die Summe: Die Belege lagen an vier getrennten
 * Stellen (Kundenlogos, MeteoSchweiz, «Vier weitere Faelle», Portfolio), und der
 * Besucher las viermal dieselbe Aussage — *das haben wir gebaut, fuer die*.
 *
 * Dieses Gefaess fasst mehrere bestehende Sektionen zu EINEM Kapitel zusammen.
 * Es ist bewusst kein Neubau einer Mega-Komponente: die Teile bleiben, was sie
 * sind, und werden hier nur unter eine Ueberschrift gestellt.
 *
 * **Das Kapitel traegt die `h2`, die Teile fallen auf `h3`.** Vier `h2` unter
 * einer Kapitelueberschrift waeren vier gleichrangige Abschnitte — genau der
 * Eindruck, den die Fusion aufloest. Die Teile bekommen dafuer ein `nested`-Flag
 * (siehe `TrustSection`, `SuccessStorySection`, `RelatedStoriesSection`,
 * `PortfolioCarouselSection`): keine eigene Flaeche, kein eigener Aussenabstand,
 * Ueberschriften eine Ebene tiefer.
 *
 * **Kein neuer Text.** Der Kapiteltitel ist die Ueberschrift seines ersten
 * Teils, nach oben gezogen — beim Beweis-Kapitel die staerkste Zeile, die die
 * Seite hat («Software, auf die sich 2.5 Millionen Menschen verlassen»). Der
 * Plan schliesst neue Sektionstexte ausdruecklich aus; neu sind allein die
 * Kapitel-Etiketten (Etappe 3).
 *
 * **Die `id` ist das Sprungziel** der Anker-Nav (Etappe 4) und steht sichtbar in
 * der URL — deshalb deutsch und sprachneutral gehalten, wie auf `/angebote`
 * (`#einstiegspfad`, `#urteilskraft`). `scroll-mt-24` haelt die Kapitelzeile
 * unter der klebenden Navigation frei; ohne das springt der Anker hinter die
 * Leiste.
 */

import React from "react";
import { SectionBadge } from "../ui";

export type ChapterSectionProps = {
    /** Sprungziel der Anker-Nav; erscheint in der URL. Deutsch, sprachneutral. */
    id: string;
    /**
     * Das Kapitel-Etikett — dasselbe Wort, das in der Sprungnavigation steht.
     *
     * Beide lesen aus `anchorNav` im Content (siehe `Homepage.tsx`), damit sie
     * nicht auseinanderlaufen.
     *
     * Dargestellt als `SectionBadge` — dasselbe Etikett wie auf allen anderen
     * Seiten, mit dem Vorgabe-Symbol. Variantenwahl wie in
     * `SpektrumSection.tsx:252`: `on-dark` auf dem Verlauf, sonst die Vorgabe.
     */
    badge?: string;
    /**
     * Die `h2` des Kapitels.
     *
     * Optional, weil sie aus dem Content kommt und dort jedes Titelfeld
     * optional ist (`section-common.ts:9`) — ein fehlender Titel darf nicht das
     * ganze Kapitel samt Inhalt verschwinden lassen. Er faellt dann aber aus
     * der Dokumentgliederung, und genau das faengt der Ueberschriften-Waechter
     * (Etappe 5): ein Kapitel ohne `h2` ist ein Content-Fehler, kein Zustand.
     */
    title?: string;
    /** Optionaler Vorspann direkt unter der Ueberschrift. */
    description?: string;
    /**
     * Der Grund — das Kapitel faerbt, seine Teile nicht.
     *
     * `gradient` ist dem Abschluss vorbehalten (und dem Hero, der kein Kapitel
     * ist): Auf einer Seite duerfen nicht drei Bloecke um denselben Rang
     * streiten, siehe `CornerstonesSection`. Teile in einem Farbverlauf-Kapitel
     * muessen ihre Schrift selbst aufhellen — das Gefaess faerbt die Flaeche,
     * nicht den Inhalt seiner Kinder.
     */
    background?: "white" | "light" | "gradient";
    children: React.ReactNode;
};

const BACKGROUNDS: Record<NonNullable<ChapterSectionProps["background"]>, string> = {
    white: "bg-white",
    light: "bg-background-light",
    gradient: "bg-gradient text-white",
};

export const ChapterSection: React.FC<ChapterSectionProps> = ({
    id,
    badge,
    title,
    description,
    background = "white",
    children,
}) => (
    <section id={id} data-testid={`chapter-${id}`} className={`scroll-mt-24 py-24 ${BACKGROUNDS[background]}`}>
        {(badge || title || description) && (
            <div className="mx-auto mb-14 max-w-3xl px-4 text-center md:px-12">
                {/* `on-dark` auf dem Verlauf: die hellen Varianten faerben Text und
                    Rand tertiary-light bzw. primary und verlieren dort den Kontrast.
                    Dieselbe Wahl wie in `SpektrumSection.tsx:252`. */}
                {badge && <SectionBadge variant={background === "gradient" ? "on-dark" : "dark"}>{badge}</SectionBadge>}
                {title && (
                    <h2
                        className={`text-4xl font-black md:text-5xl ${
                            background === "gradient" ? "text-white" : "text-black"
                        }`}
                    >
                        {title}
                    </h2>
                )}
                {description && (
                    <p className={`mt-6 text-lg ${background === "gradient" ? "text-white/80" : "text-gray-600"}`}>
                        {description}
                    </p>
                )}
            </div>
        )}
        {/*
            Der Abstand steht hier und nicht in den Teilen: Die vier Teile
            brachten je eigene `py-24` mit, was zwischen zweien 12rem ergab —
            mehr Luft als zwischen ganzen Sektionen. Im `nested`-Modus legen sie
            ihren Aussenabstand ab, das Kapitel setzt einen einheitlichen.
        */}
        <div className="space-y-20">{children}</div>
    </section>
);

export default ChapterSection;
